import { inject, Injectable } from '@angular/core';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import Clarity from '@microsoft/clarity';
import { CompanyContextService, Http, RoutingService } from '@skylab-angular-shared/core';
import { CompanyContextService as CompanyContextServiceBase, ConsoleLogger, HttpService } from '@skylab-shared/core';
import { makeScripts } from '@skylab-shared/snippets';
import { environment } from '@skylab/environments';
import { v4 as uuid } from 'uuid';
import { APPLICATION_INSIGHTS_EVENTS } from './application-insights-events';

interface AnalyticsSettings {
    googleEnabled: boolean;
    googleKey: string;
    applicationInsightsConnectionString : string;
}

@Injectable({providedIn: 'root'})
export class ApplicationInsightsService {
    private readonly SETTINGS_URL = 'analytics/settings';
    private readonly GOOGLE_ANALYTICS_URL = 'https://www.googletagmanager.com/gtag/js?id=';

    private analyticsOptions: AnalyticsSettings;
    private applicationInsights: ApplicationInsights;
    private logger = new ConsoleLogger(this.constructor.name);

    private routingService = inject(RoutingService);
    private http = inject(Http) as HttpService;
    private company = inject(CompanyContextService) as CompanyContextServiceBase;

    public trackEvent(name: APPLICATION_INSIGHTS_EVENTS, properties?: {[key:string]: any}): void {
        this.applicationInsights?.trackEvent({ name }, properties);
    }

    public trackTrace(message: string): void {
        this.applicationInsights?.trackTrace({ message });
    }

    public logException(exception: Error, severityLevel: SeverityLevel, details?: { description?: string }) {
        this.applicationInsights?.trackException({ exception, severityLevel }, details);
    }

    public async registerAsync(): Promise<void> {
        try {
            await this.setAnalitycsOptions();
            if (!this.analyticsOptions) return;

            this.registerClarity();
            this.registerApplicationInights();
            this.registerGoogleAnalitycs();
        } catch (err) {
            this.logger.warn(err);
        }
    }

    private async registerClarity(): Promise<void> {
        const clarityId = environment.clarityId;
        if (!clarityId) return;

        Clarity.init(clarityId);
        Clarity.consentV2(); //https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-without-cookie-consent
        Clarity.identify(this.company.companyContext.tenantCorrelationId, uuid());
    }

    private async setAnalitycsOptions(): Promise<void> {
        try {
            const response = await this.http.get<AnalyticsSettings>(this.SETTINGS_URL);
            this.analyticsOptions = response.data;
        } catch (err) {
            this.logger.warn(err);
        }
    };

    private registerApplicationInights(): void {
        const { applicationInsightsConnectionString  } = this.analyticsOptions;
        if (!applicationInsightsConnectionString ) return;

        this.applicationInsights = new ApplicationInsights({
            config: {
                connectionString: applicationInsightsConnectionString
            },
        });
        this.applicationInsights.loadAppInsights();

        this.routingService.states$.subscribe(data => {
            this.applicationInsights.trackPageView({uri: data.currUrl, name: data.currUrl || 'dashboard'});
        });
    }

    private registerGoogleAnalitycs(): void {
        const { googleEnabled, googleKey } = this.analyticsOptions;
        if (!googleEnabled || !googleKey) return;

        makeScripts([this.GOOGLE_ANALYTICS_URL + googleKey]);
        const win = window as any;
        win.dataLayer = win.dataLayer || [];
        win.gtag = function() {
            // eslint-disable-next-line prefer-rest-params
            win.dataLayer.push(arguments);
        };
        win.gtag('js', new Date());
        win.gtag('config', googleKey);

        this.routingService.states$.subscribe(data => {
            (window as any)?.gtag?.('event', 'page_view', {
                page_path: data.currUrl
            });
        });
    }
}
